import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { CheckCircle, Lock, Building2, User } from 'lucide-react';
import './AcceptInvite.css';

const AcceptInvite = ({ setIsAuthenticated }) => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [inviteInfo, setInviteInfo] = useState(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchInviteInfo();
  }, [token]);

  const fetchInviteInfo = async () => {
    try {
      const response = await axiosInstance.get(`/invite-info/${token}`);
      setInviteInfo(response.data);
    } catch (error) {
      setError(error.response?.data?.detail || 'Invalid or expired invitation');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    setSubmitting(true);
    
    try {
      const response = await axiosInstance.post(`/accept-invite/${token}`, { password });
      localStorage.setItem('token', response.data.access_token);
      toast.success('Account activated successfully!');
      setIsAuthenticated(true);
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to activate account');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="accept-invite-page">
        <div className="invite-card">
          <div className="loading-spinner">Loading...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="accept-invite-page">
        <div className="invite-card error-card">
          <div className="error-icon">❌</div>
          <h2>Invitation Error</h2>
          <p>{error}</p>
          <button className="btn-primary" onClick={() => navigate('/')}>
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="accept-invite-page">
      <div className="invite-card">
        <div className="invite-header">
          <div className="invite-icon">
            <CheckCircle size={48} />
          </div>
          <h1>Welcome to Southwest Permit Tracker!</h1>
          <p>You&apos;ve been invited to join your company&apos;s permit tracking system</p>
        </div>

        <div className="invite-info">
          <div className="info-item">
            <User size={20} />
            <div>
              <span className="info-label">Your Name</span>
              <span className="info-value">{inviteInfo?.full_name}</span>
            </div>
          </div>
          <div className="info-item">
            <Building2 size={20} />
            <div>
              <span className="info-label">Company</span>
              <span className="info-value">{inviteInfo?.company_name}</span>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="activate-form">
          <h2><Lock size={20} /> Set Your Password</h2>
          
          <div className="form-group">
            <label>Email</label>
            <input type="email" value={inviteInfo?.email || ''} disabled />
          </div>
          
          <div className="form-group">
            <label>Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Create a password (min 6 characters)"
              required
              minLength={6}
            />
          </div>
          
          <div className="form-group">
            <label>Confirm Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm your password"
              required
            />
          </div>
          
          <button type="submit" className="btn-activate" disabled={submitting}>
            {submitting ? 'Activating...' : 'Activate My Account'}
          </button>
        </form>

        <div className="invite-footer">
          <p>After activation, you&apos;ll be able to:</p>
          <ul>
            <li>✅ View all your permits</li>
            <li>✅ Add new permits and licenses</li>
            <li>✅ Receive expiration reminders</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AcceptInvite;
